import { useMemo, useRef, useState } from 'react'

import ArrowDownIcon from '@icons/chevron-down'
import CloseIcon from '@icons/x-mark'
import { useOnClickOutside } from '@/lib/hooks/useOnClickOutside'
import { useAuth } from '@/lib/provider/AuthProvider'
import {
  useLoadStatus,
  useStatusPageSubscription,
} from '@/lib/queries/App.queries'
import { cn } from '@/lib/misc/Utils'
import StatusPopoverBody from './status-indicator/StatusPopoverBody'
import type {
  CombinedStatusResponse,
  StatusFeedEntry,
  StatusLevel,
  StatusReportWithTimeline,
} from './status-indicator/types'
import {
  computeDisplayLevel,
  getStatusLevelStyles,
  parseStatusFeedSections,
} from './status-indicator/utils'

/**
 * The three surfaces this renders on, as a lookup table rather than a chain of
 * ternaries. `desktop`, `mobile` and `dropdown` variants existed but had no
 * call sites, so they are gone.
 */
type StatusVariant = 'toolbar' | 'sidebar' | 'dot'

const VARIANTS: Record<
  StatusVariant,
  {
    wrapper: string
    trigger: string
    /** Falsy hides the label entirely. */
    label: string | null
    /** Extra classes on the status dot, e.g. to widen its column. */
    indicator: string | null
    chevron: string | null
    /** Full popover placement, including which edge it hangs off. */
    popover: string
    /** Uses the level's tinted badge treatment. */
    tinted: boolean
  }
> = {
  toolbar: {
    wrapper: 'min-w-0',
    trigger:
      'h-8 text-neutral-50 hover:bg-neutral-20/70 hover:text-neutral-100',
    label: 'hidden min-w-0 truncate lg:block',
    indicator: null,
    chevron: 'hidden lg:block',
    popover: 'top-full mt-2 left-auto right-0',
    tinted: false,
  },
  sidebar: {
    wrapper: 'w-full min-w-0',
    // Same gutter and icon column as the sidebar nav rows: a 5px margin each
    // side gives the 8px dot the 18px slot those rows give their icons, so
    // dot centres and labels line up down the rail.
    trigger: 'w-full justify-start px-2.5 gap-2.5',
    label: 'min-w-0 flex-1 truncate text-left',
    indicator: 'mx-[5px]',
    chevron: 'block',
    // The rail is only ~256px wide and the support panel is far wider, so on
    // desktop it hangs off the rail's right edge instead of covering it. In the
    // mobile drawer there is no room beside it, so it drops below the trigger.
    popover: 'top-full mt-2 left-0 md:top-0 md:mt-0 md:left-full md:ml-3',
    tinted: true,
  },
  dot: {
    wrapper: '',
    trigger:
      'relative inline-flex size-5 shrink-0 rounded-full border border-white/50 bg-surface/10 p-0 transition hover:bg-surface/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70',
    label: null,
    indicator: null,
    chevron: null,
    popover: 'top-full mt-2 left-auto right-0',
    tinted: false,
  },
}

/**
 * Icon-only mode is a presence badge, not a row: at 68px the rail has no room
 * for a status control that reads as a peer of the nav icons, and every framed
 * variant of one looked bolted on. So it becomes a dot pinned to the logo —
 * the same convention as an avatar's online dot — and the rail's icon column
 * stays purely navigation.
 */
const StatusIndicator = ({
  variant,
  withSupport = false,
  className,
  showLabel = true,
}: {
  variant: StatusVariant
  withSupport?: boolean
  className?: string
  /** Force the icon-only form, e.g. in a collapsed sidebar rail. */
  showLabel?: boolean
}) => {
  const base = VARIANTS[variant]
  const [authState] = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const statusQuery = useLoadStatus()
  const combined: CombinedStatusResponse | null = statusQuery.data ?? null

  const userEmail = authState.user?.email?.trim() ?? ''
  const statusEmailSubscription = {
    visible: authState.active && userEmail.length > 0,
    userEmail,
  }
  const subscriptionQuery = useStatusPageSubscription(
    statusEmailSubscription.visible
  )
  const actionItemCount =
    statusEmailSubscription.visible &&
    subscriptionQuery.data?.subscribed === true &&
    subscriptionQuery.data.confirmed !== true
      ? 1
      : 0
  const actionItemLabel =
    actionItemCount === 1
      ? '1 action required'
      : `${actionItemCount} actions required`

  const parsedStatus = useMemo(() => {
    if (combined?.feed == null) {
      return {
        reportEntries: [] as StatusReportWithTimeline[],
        maintenanceEntries: [] as StatusFeedEntry[],
        feedUpdatedAt: null as string | null,
      }
    }

    const parsed = parseStatusFeedSections(combined.feed)
    return {
      reportEntries: parsed.reports,
      maintenanceEntries: parsed.maintenances,
      feedUpdatedAt: parsed.feedUpdatedAt,
    }
  }, [combined])

  const reportEntries = parsedStatus.reportEntries
  const maintenanceEntries = parsedStatus.maintenanceEntries
  const feedUpdatedAt = parsedStatus.feedUpdatedAt
  const isLoading = statusQuery.isLoading
  const hasMissingFeed = statusQuery.isSuccess && combined?.feed == null
  const hasError = statusQuery.isError || hasMissingFeed

  useOnClickOutside(dropdownRef, () => setIsOpen(false))

  const level: StatusLevel = hasError
    ? 'unknown'
    : computeDisplayLevel(reportEntries, maintenanceEntries, combined)

  const hasActiveFeed =
    reportEntries.length > 0 || maintenanceEntries.length > 0

  const isAllQuiet = !isLoading && !hasError && !hasActiveFeed

  /** Full summary header hidden: single clear “all good” state with link to public history. */
  const showAllGoodPopoverLayout =
    isAllQuiet &&
    level === 'operational' &&
    !combined?.has_ongoing_maintenance &&
    !combined?.has_planned_maintenance

  const latestTimestamp = (() => {
    const dates = [
      ...reportEntries.map((r) => r.sortDate),
      ...maintenanceEntries.map((m) => m.date),
    ].filter(Boolean)
    if (dates.length === 0) return feedUpdatedAt
    return dates.reduce((best, d) =>
      new Date(d).getTime() > new Date(best).getTime() ? d : best
    )
  })()

  const levelStyles = getStatusLevelStyles(level as StatusLevel)

  const label = isLoading
    ? 'Checking status\u2026'
    : hasError
      ? 'Status unavailable'
      : levelStyles.label

  const v = showLabel
    ? base
    : {
        ...base,
        label: null,
        chevron: null,
        indicator: null,
        tinted: false,
        // The button *is* the dot. The canvas-coloured ring lifts it off
        // whatever it is pinned to, the way an avatar badge does.
        trigger:
          'size-3.5 shrink-0 justify-center rounded-full p-0 ring-2 ring-canvas hover:brightness-95',
        wrapper: 'flex',
      }

  const badgeBaseClasses = cn(
    'inline-flex h-9 items-center rounded-lg text-sm font-medium leading-5 cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-60/40',
    'gap-2 px-3'
  )

  const triggerClasses = cn(
    'relative',
    variant !== 'dot' && badgeBaseClasses,
    v.tinted && levelStyles.badgeClass,
    v.trigger,
    isOpen &&
      (variant === 'dot'
        ? 'ring-2 ring-white/70'
        : showLabel
          ? 'bg-neutral-20/70'
          : // The dot has no room for a fill change, so the ring carries the
            // open state instead.
            'ring-accent-60')
  )

  return (
    <div className={cn('relative', v.wrapper, className)} ref={dropdownRef}>
      {/* Trigger badge */}
      <button
        type="button"
        className={triggerClasses}
        onClick={() => setIsOpen((open) => !open)}
        aria-label={
          v.label == null
            ? actionItemCount > 0
              ? `${label}, ${actionItemLabel}`
              : label
            : undefined
        }
      >
        {showLabel ? (
          <span
            className={cn(
              'block size-2 shrink-0 rounded-full shadow-inner origin-center',
              levelStyles.dotClass,
              v.indicator,
              (statusQuery.isFetching || statusQuery.isLoading) &&
                'animate-[pulse_1.6s_ease-in-out_infinite]',
              variant === 'dot' &&
                'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
            )}
          />
        ) : (
          <span
            className={cn(
              'block size-full rounded-full',
              levelStyles.dotClass,
              (statusQuery.isFetching || statusQuery.isLoading) &&
                'animate-[pulse_1.6s_ease-in-out_infinite]'
            )}
          />
        )}
        {v.label && <span className={v.label}>{label}</span>}
        {v.chevron && (
          <ArrowDownIcon
            className={cn('h-3 w-3 shrink-0 opacity-70', v.chevron)}
          />
        )}
        {actionItemCount > 0 &&
          (showLabel ? (
            <span
              className="absolute -right-1.5 -top-1.5 flex size-4 items-center justify-center rounded-full bg-status-warning-500 text-2xs font-bold leading-none text-status-warning-900 ring-2 ring-canvas"
              data-cy="status-action-item-badge"
            >
              <span aria-hidden>!</span>
              {v.label && <span className="sr-only">{actionItemLabel}</span>}
            </span>
          ) : (
            <span
              className="absolute -right-1 -top-1 size-2 rounded-full bg-status-warning-500 ring-2 ring-canvas"
              data-cy="status-action-item-badge"
              aria-hidden
            />
          ))}
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute z-60 overflow-hidden rounded-2xl shadow-popover',
            'max-md:fixed max-md:inset-x-3 max-md:top-16 max-md:bottom-3 max-md:w-auto max-md:overflow-y-auto',
            withSupport
              ? 'w-[min(100vw-1.5rem,52rem)]'
              : 'w-[min(100vw-1.5rem,28rem)]',
            // Hanging off the rail costs the panel the rail's own width, so
            // reserve it here rather than letting the panel run off screen.
            variant === 'sidebar' &&
              (withSupport
                ? 'md:w-[min(calc(100vw-20rem),52rem)]'
                : 'md:w-[min(calc(100vw-20rem),28rem)]'),
            v.popover
          )}
          role="dialog"
          aria-label="Service status and support"
        >
          <button
            type="button"
            aria-label="Close"
            onClick={() => setIsOpen(false)}
            className="absolute right-2 top-2 z-50 flex size-10 items-center justify-center rounded-full bg-black/30 text-white md:hidden"
          >
            <CloseIcon className="size-5" />
          </button>
          <StatusPopoverBody
            isPopoverOpen={isOpen}
            statusEmailSubscription={statusEmailSubscription}
            withSupport={withSupport}
            showAllGoodPopoverLayout={showAllGoodPopoverLayout}
            latestTimestamp={latestTimestamp}
            level={level}
            isLoading={isLoading}
            hasError={hasError}
            combined={combined}
            reportEntries={reportEntries}
            maintenanceEntries={maintenanceEntries}
            hasActiveFeed={hasActiveFeed}
            isAllQuiet={isAllQuiet}
          />
        </div>
      )}
    </div>
  )
}

export default StatusIndicator
